import type { I18n } from '@lingui/core'
import { withI18n } from '@lingui/react'
import classNames from 'classnames'
import { type FC } from 'react'
import { compose } from 'redux'

// eslint-disable-next-line no-restricted-imports
import {
  type OfferV1,
  PrescriptionTypes,
} from 'server/utils/pimServiceUtilsTypes'
import AttributePicker from 'views/containers/browser/ProductVariantsAttributesContainer/AttributePicker'
import withShopAndRuntimeConfig from 'views/enhancers/withShopAndRuntimeConfig'

import AttributePickerRegionCollapsibleWrapper from './AttributePickers/AttributePickerRegionCollapsibleWrapper'
import ExcludedAttributeValue from './AttributePickers/ExcludedAttributeValue'

import './ProductVariantsAttributesContainer.scss'

interface ProductVariantAttributesProps {
  i18n: I18n
  product: Product
  variants: Product[]
  isMasterPage: boolean
  prescriptionType: string
}
export interface Product {
  allAttributes: []
  variantAttributes: VariantAttribute[]
  upid: string
  variants: Variant[]
  stockStatus: StockStatus
  prices: Prices[]
  discount: number
  unitQuantityString: string
  unit: string
  variantAttributesExcluded: VariantAttributesExcluded[]
}

export interface VariantAttributesExcluded {
  code: string
  value: string
  order: number
}

export interface Prices {
  amount: number
  currency: string
  formatted: string
  net: number
  type: string
  bonus?: number
}

export interface Variant {
  stockStatus: StockStatus
  variantAttributes: VariantAttribute[]
  upid: string
  prices: Prices[]
  discount: number
  offers?: OfferV1[]
  offerId?: string
}

export interface StockStatus {
  statusReason: string
}

export interface VariantAttribute {
  label: string
  type: string
  value: string
  variantCode: string
  values: string[]
}

export interface VariantDetails {
  label: string
  code: string
  value: string
  order: number
  type: string
}

export interface ProductAttributes {
  label: string
  type: string
  personal: boolean
  code: string
  values: string[]
  variantCode: string
}

interface SelectedAttributes {
  [key: string]: string | string[]
}
interface InStockFields {
  [key: string]: string[]
}

interface CurrentVariant {
  [key: string]: string | undefined
}

const ProductVariantAttributes: FC<ProductVariantAttributesProps> = ({
  i18n,
  variants,
  product,
  isMasterPage = false,
  prescriptionType,
}) => {
  const selectedVariant = variants.find((variant: Product) => {
    return variant.upid === product.upid
  })
  const selectedAttributes: SelectedAttributes = {}
  const getSelectedAttributes = () => {
    selectedVariant?.variantAttributes.map(variantAttribute => {
      selectedAttributes[variantAttribute.variantCode] = variantAttribute.value
    })
  }
  getSelectedAttributes()

  const attributesToRender = selectedVariant?.allAttributes
  const isSingleAttributeProduct =
    attributesToRender && attributesToRender.length < 2
  const selectedVariantAttributes = selectedVariant?.variantAttributes
  const inStockFields: InStockFields = {}
  const availableVariants = selectedVariant?.variants.filter(variant => {
    return (
      variant.stockStatus?.statusReason === 'general.label.product.available'
    )
  })

  const allVariantAttributes = []
  const getAllVariantAttributes = () => {
    selectedVariant?.variants.forEach(variant => {
      const currentVariant: CurrentVariant = {}
      variant.variantAttributes?.forEach(variantAttribute => {
        currentVariant[variantAttribute.variantCode] = variantAttribute.value
      })
      currentVariant['upid'] = variant.upid
      allVariantAttributes.push(currentVariant)
    })
  }

  const getInStockFields = () => {
    Object.keys(selectedAttributes).forEach(key => {
      inStockFields[key] = []
      const tempSelectedVariant = { ...selectedAttributes }
      delete tempSelectedVariant['upid']
      availableVariants?.forEach(availableVariant => {
        let exist = true
        let value = ''
        availableVariant?.variantAttributes.forEach(
          availableVariantAttribute => {
            if (
              availableVariantAttribute.variantCode !== key &&
              tempSelectedVariant[availableVariantAttribute.variantCode] !==
                availableVariantAttribute.value
            ) {
              exist = false
            }
            if (availableVariantAttribute.variantCode === key) {
              value = availableVariantAttribute.value
            }
          }
        )
        if (exist) {
          inStockFields[key].push(value)
        }
      })
    })
  }
  getAllVariantAttributes()
  getInStockFields()

  const isSelectedAttributes = (val: string, key: string) => {
    return !!selectedVariantAttributes?.find(selectedAttribute => {
      return (
        selectedAttribute.value === val && selectedAttribute.variantCode === key
      )
    })
  }

  const findVariant = (variantAttributeValue: string) => {
    return selectedVariant?.variants.find(variant => {
      return variant.variantAttributes[0].value === variantAttributeValue
    })
  }

  const getVariantForSingleAttribute = (variantAttributeValue: string) => {
    const singleVariant = findVariant(variantAttributeValue)
    return variants.find(variant => {
      return variant.upid === singleVariant?.upid
    })
  }

  const checkIfInStock = (attributeValue: string, attributeCode: string) => {
    return !!inStockFields[attributeCode]?.find(x => {
      return x === attributeValue
    })
  }

  const packageSizeAttribute = attributesToRender?.find(
    (variant: ProductAttributes) => {
      return variant.code === 'package_size'
    }
  )

  const hasAdditionalCharge: boolean =
    prescriptionType === PrescriptionTypes.INSURANCE_PRESCRIPTION ||
    prescriptionType === PrescriptionTypes.FREE_PRESCRIPTION

  const singleVariantAttributeProductWrapperClasses = classNames(
    'l-flex l-flex--wrap attributePickers l-grid@medium l-grid--tighter@medium',
    {
      'u-no-margin--left@small-tiny': isSingleAttributeProduct,
    }
  )

  const singleVariantAttributeProductClasses = classNames(
    'u-no-padding--left@small-tiny u-margin-small--right@small-tiny l-grid__item',
    {
      'l-col--3-of-12@large l-col--4-of-12@medium':
        packageSizeAttribute && isSingleAttributeProduct,
      'l-col--6-of-12@large':
        !packageSizeAttribute || !isSingleAttributeProduct,
    }
  )

  const variantAttributesExcludedStyles = classNames(
    'o-productVariantsAttributeExcluded--border--top',
    {
      'o-productVariantsAttributeExcluded--border--bottom':
        attributesToRender && attributesToRender?.length > 0,
    }
  )

  const order = [
    'package_size',
    'color_number',
    'size',
    'cannula_length',
    'tube_length',
    'resistance',
    'book_type',
    'flavor',
    'cbd_content',
    'patch_size',
    'nominal_weight',
  ]

  const sortedExcluded =
    selectedVariant?.variantAttributesExcluded &&
    selectedVariant.variantAttributesExcluded.sort(
      (a, b) => order.indexOf(a.code) - order.indexOf(b.code)
    )
  return (
    <div className="o-variantAttributesContainerWrapper" id="topOfPickers">
      {!!sortedExcluded?.length && (
        <div className={variantAttributesExcludedStyles}>
          {sortedExcluded.map(element => {
            return (
              <ExcludedAttributeValue
                attributeValue={element.value}
                code={element.code}
              />
            )
          })}
        </div>
      )}
      {attributesToRender?.map(
        (variantAttribute: ProductAttributes, index: number) => {
          return (
            <div
              className="attributeRegion u-padding--bottom u-padding--top"
              data-qa-id={`variantAttribute-attributeRegion`}
              key={variantAttribute.code}
              id={variantAttribute.code}
            >
              <AttributePickerRegionCollapsibleWrapper
                i18n={i18n}
                variantAttribute={variantAttribute}
                isSingleAttributeProduct={!!isSingleAttributeProduct}
                packageSizeAttribute={!!packageSizeAttribute}
                selectedVariant={selectedVariant}
                isMasterPage={isMasterPage}
              >
                <div
                  className={singleVariantAttributeProductWrapperClasses}
                  id={variantAttribute.code}
                  data-qa-id={`variantAttribute-${index}`}
                >
                  {variantAttribute.values.map(
                    (variantAttributeValue: string) => {
                      let pickerVariant: Variant | undefined
                      if (isSingleAttributeProduct) {
                        pickerVariant = getVariantForSingleAttribute(
                          variantAttributeValue
                        )
                      }
                      const isSelected = isSelectedAttributes(
                        variantAttributeValue,
                        variantAttribute.code
                      )
                      return (
                        <div className={singleVariantAttributeProductClasses}>
                          <AttributePicker
                            isSelected={isSelected}
                            isInStock={checkIfInStock(
                              variantAttributeValue,
                              variantAttribute.code
                            )}
                            isMasterPage={isMasterPage}
                            isPersonal={variantAttribute.personal}
                            isSingle={variantAttribute.values.length < 2}
                            pickerValue={variantAttributeValue}
                            id={variantAttribute.code}
                            key={variantAttributeValue}
                            variant={pickerVariant}
                            variantAttributeType={variantAttribute.type}
                            isSingleAttributeProduct={
                              !!isSingleAttributeProduct
                            }
                            prescriptionType={prescriptionType}
                            hasAdditionalCharge={hasAdditionalCharge}
                            packageSizeAttribute={!!packageSizeAttribute}
                          />
                        </div>
                      )
                    }
                  )}
                </div>
              </AttributePickerRegionCollapsibleWrapper>
            </div>
          )
        }
      )}
    </div>
  )
}
export default compose(
  withI18n(),
  withShopAndRuntimeConfig
)(ProductVariantAttributes)
